import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import {
  getProductsRequestFromQueryParams,
  getProductsRequestToQueryParams,
  ProductOrderField,
} from '../../interfaces/Product/GetProductsRequest';
import { downloadProductsExcel, getProducts } from '../../api/Product';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { ProductSummariesTable } from './ProductSummariesTable';
import { SortingOrder } from '../../interfaces/SortingOrder';
import { Pagination } from '../../interfaces/Pagination';
import { SearchBox } from '../../components/SearchBox/SearchBox';
import { getUrlWithQueryParameters } from '../../utils/QueryParameter';
import { BulkPriceUploadDialog } from './UpdateProduct/BulkPriceUploadDialog/BulkPriceUploadDialog';

export const ProductSummariesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(
    getProductsRequestFromQueryParams(new URLSearchParams(location.search)),
  );
  const [loading, setLoading] = useState(false);
  const [summaries, setSummaries] = useState<PaginatedResponse<ProductSummary>>(
    {
      data: [],
      totalCount: 0,
    },
  );
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const refreshSummaries = () => {
    setLoading(true);
    getProducts(filters)
      .then((p) => setSummaries(p))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshSummaries();

    const queryParams = getProductsRequestToQueryParams(filters);
    const newUrl = getUrlWithQueryParameters(queryParams);
    window.history.pushState({ path: newUrl }, '', newUrl);
  }, [filters]);

  const handleDownload = useCallback(() => {
    setLoading(true);
    downloadProductsExcel()
      .then(() => {
        toast.success('Fiyat listesi başarıyla indirildi');
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = useCallback(
    (s: string) => {
      setFilters({ ...filters, search: s, page: 0 });
    },
    [filters],
  );

  const handleChangeOrder = useCallback(
    (newOrderBy: ProductOrderField, newOrder: SortingOrder) =>
      setFilters({
        ...filters,
        orderBy: newOrderBy,
        order: newOrder,
      }),
    [filters],
  );

  const handleChangePagination = useCallback(
    (newPagination: Pagination) => setFilters({ ...filters, ...newPagination }),
    [filters],
  );

  return (
    <Box sx={{ ...sharedStyles.pageBody, p: 3 }}>
      <BulkPriceUploadDialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onUpload={() => {
          setShowUploadDialog(false);
          refreshSummaries();
        }}
      />

      <Typography variant='h5' sx={{ ...sharedStyles.pageTitle }}>
        Katalog
      </Typography>
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 1.5,
        }}
      >
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => navigate('/products/catalog/new')}
          sx={{ ...sharedStyles.buttonText }}
        >
          Yeni Ürün
        </Button>

        <Button
          variant='contained'
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ ...sharedStyles.buttonText }}
        >
          Fiyat Listesi
        </Button>

        <Button
          variant='contained'
          startIcon={<UploadIcon />}
          onClick={() => setShowUploadDialog(true)}
          sx={{ ...sharedStyles.buttonText }}
        >
          Toplu Fiyat Güncellemesi
        </Button>

        <Box sx={{ flexGrow: 1 }} />
      </Box>

      <Paper sx={{ mb: 1.5, p: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <SearchBox
            placeholder='Ürün Ara'
            initialValue={filters.search}
            onChange={handleSearch}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={filters.includeInactives}
                onChange={() =>
                  setFilters({
                    ...filters,
                    includeInactives: !filters.includeInactives,
                  })
                }
              />
            }
            label={
              <Typography sx={sharedStyles.subtitle2}>
                Pasifleri dahil et
              </Typography>
            }
          />
        </Box>
      </Paper>

      <ProductSummariesTable
        productSummaries={summaries.data}
        existingFilters={filters}
        totalCount={summaries.totalCount}
        orderBy={filters.orderBy}
        order={filters.order}
        onChangeOrder={handleChangeOrder}
        pagination={filters}
        onPagination={handleChangePagination}
      />
    </Box>
  );
};
